/**
 * Shared router-vendor dropdown definitions.
 *
 * The BE `Router.model_name` field
 * (services/plugins/equipment_discovery/models.py:225) is a free-text
 * CharField — no choices restriction. The dropdown is purely a FE UX
 * affordance: pick a known vendor for the common case, fall back to
 * "other" + a text input for one-offs. Custom values flow through to
 * the BE unchanged.
 *
 * When loading an existing record with an out-of-list `model_name`
 * (e.g. "draytek" typed before the dropdown existed),
 * `pickRouterModelDropdownValue` returns `'other'` and the form
 * prefills the other-text input with the existing value.
 */

export const ROUTER_MODEL_DROPDOWN_VALUES = [
  'keenetic',
  'linza_router',
  'mikrotik',
  'asus',
  'tplink',
  'other',
] as const;

export type RouterModelDropdownValue = (typeof ROUTER_MODEL_DROPDOWN_VALUES)[number];

const KNOWN_VENDOR_VALUES: ReadonlySet<RouterModelDropdownValue> = new Set([
  'keenetic',
  'linza_router',
  'mikrotik',
  'asus',
  'tplink',
]);

export const ROUTER_MODEL_DROPDOWN_LABEL_KEYS: Record<RouterModelDropdownValue, string> = {
  keenetic: 'equipment.router.modelName.keenetic',
  linza_router: 'equipment.router.modelName.linzaRouter',
  mikrotik: 'equipment.router.modelName.mikrotik',
  asus: 'equipment.router.modelName.asus',
  tplink: 'equipment.router.modelName.tplink',
  other: 'equipment.router.modelName.other',
};

/** Router roles that are neighbour-bridge ("impostor") setups: placed as a
 *  plain client in someone else's LAN, forwarding a NEIGHBOUR's camera to us
 *  over WG via masquerade, without touching the client's own router. The full
 *  console recipe lives in docs/router-impostor-bridge.md. */
export function isImpostorModel(value: RouterModelDropdownValue): boolean {
  return value === 'linza_router';
}

/**
 * Map a stored `model_name` to a dropdown value.
 *
 * - Empty string → `'keenetic'` (the most common case; matches the
 *   prior placeholder hint "keenetic, mikrotik, …" first item).
 * - Exact case-insensitive match → that vendor.
 * - Anything else → `'other'`.
 */
export function pickRouterModelDropdownValue(modelName: string): RouterModelDropdownValue {
  if (!modelName) return 'keenetic';
  const normalized = modelName.trim().toLowerCase();
  if (KNOWN_VENDOR_VALUES.has(normalized as RouterModelDropdownValue)) {
    return normalized as RouterModelDropdownValue;
  }
  return 'other';
}

/**
 * Resolve the dropdown + other-text into the value we send to the BE.
 *
 * - For a known vendor: send the lowercase vendor name.
 * - For "other": send the trimmed other-text input.
 */
export function resolveRouterModelName(
  dropdown: RouterModelDropdownValue,
  otherText: string,
): string {
  if (dropdown === 'other') return otherText.trim();
  return dropdown;
}
